import { Injectable } from '@angular/core';
import { OrderItem } from 'com.trumbull.phx.order.api-client';
import { OrderDetails } from '../../orders/order-details/order-details.model';
import { RootObject } from '../../orders/order-details/orders.model';
import { TenantService } from '../../tenant.service';
import _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class OrderDetailsMapperService {

  vocabData: any;

  mapOrderDetails(order: OrderItem, data: RootObject, vocabData: any): OrderDetails {
    let result = new OrderDetails();
    const selectedTenant = localStorage.getItem('selectedTenant') || '';

    const caseData = data.case;
    const retrievalData = data.retrievalObjective;
    const attachmentsData = data.attachments;

    result.id = order.orderNumber || '';
    result.claimNumber = caseData.referenceNumber ?? 'Unknown';
    result.dateOfIncident = this.formatDate(caseData.incidentDate); // format as mm/dd/yyyy
    result.carrierName = selectedTenant;
    result.typeOfOrder = this.getOrderTypeDescription(retrievalData.orderTypeCode, vocabData) ?? 'Unknown';
    result.serviceLevel = retrievalData.serviceLevelCode ?? 'Normal';
    result.orderedDate = this.formatDate(order.createdDate);
    this.vocabData = vocabData;
    this.getContactDetails(caseData.parties, result);
    this.getRetrievalDetails(retrievalData, result);
    this.getAttachmentDetails(attachmentsData, result);

    return result;
  }

  formatDate(date?: string) {
    if (date == null) {
      return "Unknown";
    }

    const dateObj = new Date(date);
    return `${dateObj.getMonth() + 1}/${dateObj.getDate()}/${dateObj.getFullYear()}`;
  }

  getOrderTypeDescription(orderType: string = '', vocabData: any) {
    const vocabOrderData = vocabData?.find((d: any) => d.vocabularyName === 'OrderType');
    const selectedType = vocabOrderData?.vocabularyItems?.find((item: any) => item?.code === orderType);
    return selectedType?.description;
  }

  getTitleDescription(title: string = '', vocabData: any) {
    const vocabTitleData = vocabData?.find((d: any) => d.vocabularyName === 'ContactTitle');
    const selectedTitle = vocabTitleData?.vocabularyItems?.find((item: any) => item?.code === title);
    return selectedTitle?.description;
  }

  getStateDescription(city: string = '', vocabData: any) {
    const vocabCityData = vocabData?.find((d: any) => d.vocabularyName === 'PhysicalLocationUsAddressStateCode');
    const selectedCity = vocabCityData?.vocabularyItems?.find((item: any) => item?.code === city);
    return selectedCity?.description;
  }

  getContactDetails(parties: any, result: OrderDetails) {
    parties.forEach((element: any) => {
      const distinctRoleCodes = _.uniq(_.map(element.roles, 'roleCode'));

      if (distinctRoleCodes.length === 1) {
        if (distinctRoleCodes[0] === 'defendant-attorney' || distinctRoleCodes[0] === 'defendant-paralegal' || distinctRoleCodes[0] === 'contact-other') {
          this.addContactToDisplay(element, result);
        }
        if (distinctRoleCodes[0] === 'defendant') {
          this.addDefendentDataToDisplay(element, result);
        }
        if (distinctRoleCodes[0] === 'plaintiff-attorney') {
          this.addCounselInfoToDisplay(element, result);
        }
        if (distinctRoleCodes[0] === 'court') {
          this.addCourtDetailsToDisplay(element, result);
        }
      }
    });
  }

  addContactToDisplay(element: any, result: OrderDetails) {
    let displayName: string = element.party.displayName;
    let email: string = '';
    let phone!: number;
    let extension!: string;
    let title: string = '';
    let barNumber: string = '';

    element.party.locators.forEach((locator: any) => {
      if (locator.email) {
        email = locator.email.address;
      }
      if (locator.phone) {
        phone = locator.phone.number;
        extension = locator.phone.extension;
      }
    });

    element.party.names.forEach((names: any) => {
      if (names.function === 'title') {
        title = this.getTitleDescription(names.text, this.vocabData);
        // title = names.text;
      }
    });

    element.roles.forEach((role: any) => {
      barNumber = role?.attorney?.barNumber;
    })

    const newContact = {
      displayName: displayName,
      phone: phone,
      extension: extension,
      email: email,
      title: title,
      barNumber: barNumber
    };
    result.contacts.push(newContact);
  }

  getRetrievalDetails(retrievalData: any, result: OrderDetails) {
    result.specialInstructions = retrievalData.specialInstructions;

    retrievalData.parties.forEach((element: any) => {
      if (element.roles.some((role: any) => role.roleCode === 'bill-to')) {
        this.addBillingDetailsToDisplay(element, result);
      }
    });

    retrievalData.parties.forEach((element: any) => {
      if (element.roles.some((role: any) => role.roleCode === 'deliver-to')) {
        this.addDeliveryDetailsToDisplay(element, result);
      }
    });

    retrievalData.parties.forEach((element: any) => {
      if (element.roles.some((role: any) => role.roleCode === 'record-subject')) {
        this.addRecordSubjectDetailsToDisplay(element, result);
      }
    });

    retrievalData.parties.forEach((element: any) => {
      if (element.roles.some((role: any) => role.roleCode === 'records-custodian')) {
        this.addProviderInfoToDisplay(element, retrievalData, result);
      }
    });

    retrievalData.parties.forEach((element: any) => {
      if (element.roles.some((role: any) => role.roleCode === 'medical-provider')) {
        this.addMedicalProviderDetailsToDisplay(element, result);
      }
    });
  }

  addBillingDetailsToDisplay(element: any, result: OrderDetails) {
    let email: string = '';
    let billingContactName: string = element.party.displayName;

    element.party.locators.forEach((locator: any) => {
      if (locator.email) {
        email = locator.email.address;
      }
    });
    const billDetail = {
      email: email,
      billingContactName: billingContactName
    };
    result.billingInfo = billDetail;
  }

  addDeliveryDetailsToDisplay(element: any, result: OrderDetails) {
    let attentionTo: string = '';
    let firmTo: string = element.party.displayName;
    let phone: string = '';
    let extension: string = '';
    let address: string = '';

    element.party.names.forEach((names: any) => {
      if (names.function === 'contact-name') {
        attentionTo = names.text;
      }
    });

    element.party.locators.forEach((locator: any) => {
      if (locator.phone) {
        locator.phone.purpose === 'work' ? phone = locator.phone.number : '';
        locator.phone.purpose === 'work' ? extension = locator.phone.extension : '';
      }
      if (locator.location) {
        address = (locator.location.location.usAddress.lineOne ? locator.location.location.usAddress.lineOne + ', ' : '')
          + (locator.location.location.usAddress.city ? locator.location.location.usAddress.city + ', ' : '')
          + (locator.location.location.usAddress.stateCode ? this.getStateDescription(locator.location.location.usAddress.stateCode, this.vocabData) + ', ' : '')
          + (locator.location.location.usAddress.postalCode ? locator.location.location.usAddress.postalCode : '');
      }
    });

    const deliveryDetail = {
      attentionTo: attentionTo,
      firmTo: firmTo,
      phone: phone,
      extension: extension,
      address: address
    };

    result.deliveryInfo = deliveryDetail;
  }

  getAttachmentDetails(attachmentsData: any, result: OrderDetails) {
    let storageId: string = '';
    let storageProviderId: string = '';
    let contentType: string = '';
    let fileName: string = '';
    let displayName: string = '';
    let description: string = '';
    let sizeBytes!: number;
    let sizeText: string = '';

    attachmentsData.forEach((element: any) => {
      if (element.document) {
        storageId = element.document.storageId;
        storageProviderId = element.document.storageProviderId;
        contentType = element.document.contentType;
        fileName = element.document.fileName;
        description = element.document.description;
        sizeBytes = element.document.sizeBytes;
        sizeText = element.document.sizeText;
        displayName = element.document.displayName;
      }

      const attachmentDetail = {
        storageId: storageId,
        storageProviderId: storageProviderId,
        contentType: contentType,
        fileName: fileName,
        displayName: displayName,
        description: description,
        sizeBytes: sizeBytes,
        sizeText: sizeText
      };
      // result.attachmentInfo.push(attachmentDetail);
    });
  }

  addRecordSubjectDetailsToDisplay(element: any, result: OrderDetails) {
    element.roles.forEach((role: any) => {
      if (role.roleCode === 'record-subject') {
        let name: string = element.party.displayName;
        let alias: string = '';
        let dateOfBirth: string = '';
        let lastDigitOfSSN: string = '';
        let dateOfDeath: string = '';

        element.party.names.forEach((names: any) => {
          if (names.function === 'alias') {
            alias = names.text;
          }
        });
        dateOfBirth = element.party.person.dateOfBirth;
        lastDigitOfSSN = role.recordSubject.socialSecurityNumber.slice(-4);

        dateOfDeath = element.party.person.dateOfDeath;

        let requestNumbers = role.requestNumbers;

        const recordSubject = {
          name: name,
          alias: alias,
          dateOfBirth: dateOfBirth,
          lastDigitOfSSN: lastDigitOfSSN,
          dateOfDeath: dateOfDeath,
          requestNumbers: requestNumbers
        };

        result.subjects.push(recordSubject);
      }
    });
  }

  addDefendentDataToDisplay(element: any, result: OrderDetails) {
    let name: string = element.party.displayName;
    let address: string = '';

    element.party.locators.forEach((locator: any) => {
      address = (locator.location.location.usAddress.lineOne ? locator.location.location.usAddress.lineOne + ', ' : '')
        + (locator.location.location.usAddress.city ? locator.location.location.usAddress.city + ', ' : '')
        + (locator.location.location.usAddress.stateCode ? this.getStateDescription(locator.location.location.usAddress.stateCode, this.vocabData) + ', ' : '')
        + (locator.location.location.usAddress.postalCode ? locator.location.location.usAddress.postalCode : '');
    });

    const defendant = {
      name: name,
      address: address
    };

    result.subpoena.defendant.push(defendant);
  }

  addCounselInfoToDisplay(element: any, result: OrderDetails) {
    let recordSubjectName: string = '';
    let attorneyName: string = '';
    let firmName: string = '';
    let address: string = '';
    let phone: string = '';
    let fax: string = '';
    let email: string = '';

    if(element.roles.length === 1) {
      element.roles.forEach((item: any) => {
        recordSubjectName = item.attorney.representing;
      });
    }
    else{
      element.roles.forEach((item: any) => {
        if(recordSubjectName === '') {
          recordSubjectName = item.attorney.representing;
        }
        else{
          recordSubjectName = recordSubjectName + ", " + item.attorney.representing;
        }
      });
    }
    element.party.names.forEach((names: any) => {
      if (names.function === 'business-name') {
        firmName = names.text;
      }
      else if (names.function === 'contact-name') {
        attorneyName = names.text;
      }
    });

    element.party.locators.forEach((locator: any) => {
      if (locator.typeCode === 'email') {
        email = locator.email.address;
      }
      else if (locator.phone) {
        if (locator.phone.purpose === 'work') {
          phone = locator.phone.number;
        }
        if (locator.phone.purpose === 'fax') {
          fax = locator.phone.number;
        }
      }
      else if (locator.typeCode === 'location') {
        address = (locator.location.location.usAddress.lineOne ? locator.location.location.usAddress.lineOne + ', ' : '')
          + (locator.location.location.usAddress.city ? locator.location.location.usAddress.city + ', ' : '')
          + (locator.location.location.usAddress.stateCode ? this.getStateDescription(locator.location.location.usAddress.stateCode, this.vocabData) + ', ' : '')
          + (locator.location.location.usAddress.postalCode ? locator.location.location.usAddress.postalCode : '');
      }
    });
    
    const counselInfo = {
      recordSubjectName: recordSubjectName,
      attorneyName: attorneyName,
      firmName: firmName,
      address: address,
      phone: phone,
      fax: fax,
      email: email
    };
    result.subpoena.counselInfo.push(counselInfo);
    result.counselInfo.push(counselInfo);
  }

  addCourtDetailsToDisplay(element: any, result: OrderDetails) {
    let judge: string = '';
    let court: string = element.party.displayName;
    let courtAddress: string = '';
    let jurisdictionCounty: string = '';
    let state: string = '';

    element.roles.forEach((role: any) => {
      judge = role.court.judgeName;
      jurisdictionCounty = role.court.jurisdiction;
    });

    element.party.locators.forEach((locator: any) => {
      if (locator.typeCode === 'location') {
        courtAddress = (locator.location.location.usAddress.lineOne ? locator.location.location.usAddress.lineOne + ', ' : '')
          + (locator.location.location.usAddress.city ? locator.location.location.usAddress.city + ', ' : '')
          + (locator.location.location.usAddress.stateCode ? this.getStateDescription(locator.location.location.usAddress.stateCode, this.vocabData) + ', ' : '')
          + (locator.location.location.usAddress.postalCode ? locator.location.location.usAddress.postalCode : '');
      }
    });

    result.subpoena.judge = judge;
    result.subpoena.court = court;
    result.subpoena.courtAddress = courtAddress;
    result.subpoena.jurisdictionCounty = jurisdictionCounty;
    result.subpoena.state = state;
  }

  addProviderInfoToDisplay(element: any, retrievalData: any, result: OrderDetails) {
    let providerInfo: any = {};
    let requestNumber: number;
    let provider: string = element.party.displayName;
    let address: string = '';
    let email: string = '';
    let recordType: string = '';
    let recordSubject: string = '';
    let doctor: string = '';
    let subpoenaLanguage: string = '';
    let recordRange: string = '';
    let notes: string = '';
    let ambulanceDateOfService: string = '';
    let ambulanceTimeOfService: string = '';
    let ambulancePickupAddress: string = '';
    let ambulanceDropAddress: string = '';
    let militaryBranch: string = '';
    let militaryServiceNumber: string = '';
    let va: string = '';
    let unionMembershipNumber: string = '';
    let otherDescriptionOfRecords: string = '';
    let diagnosticTypeCodes: string = '';
    let diagnosticComments: string = '';
    let diagnosticFocusArea: string = '';
    let insuranceClaimNumber: string = '';
    let insurancePolicyNumber: string = '';
    let employmentSSN: string = '';
    let irsSSN: string = '';
    let socialSecuritySSN: string = '';
    let ssnAddress: string = '';

    element.roles.forEach((role: any) => {
      if (role.roleCode === 'records-custodian' && role.requestNumbers) {
        role.requestNumbers.forEach((requestNumber: any) => {
          element.party.locators.forEach((locator: any) => {
            if (locator.typeCode === 'email') {
              email = locator.email.address;
            }

            if (locator.typeCode === 'location') {
              address = (locator.location.location.usAddress.lineOne ? locator.location.location.usAddress.lineOne + ', ' : '')
                + (locator.location.location.usAddress.city ? locator.location.location.usAddress.city + ', ' : '')
                + (locator.location.location.usAddress.stateCode ? this.getStateDescription(locator.location.location.usAddress.stateCode, this.vocabData) + ', ' : '')
                + (locator.location.location.usAddress.postalCode ? locator.location.location.usAddress.postalCode : '');
            }
          });

          recordSubject = result.subjects.find((subject: any) => subject.requestNumbers?.includes(requestNumber))?.name ?? '';

          retrievalData.requests.forEach((request: any) => {
            if (request.requestNumber === requestNumber) {
              request.scope.forEach((scope: any) => {
                recordRange = (scope.beginDate && scope.endDate) ? (this.formatDate(scope.beginDate) + ' - ' + this.formatDate(scope.endDate)) : 'Any or All';
                if (recordType === '') {
                  recordType = scope.recordTypeCode;
                }
                else {
                  recordType = recordType + ', ' + scope.recordTypeCode;
                }

                if (scope.recordTypeCode === 'ambulance') {
                  ambulanceDateOfService = scope.qualifiers.ambulanceDateOfService;
                  ambulanceTimeOfService = scope.qualifiers.ambulanceTimeOfService;
                  ambulancePickupAddress = scope.qualifiers.ambulancePickupLine1 + ', ' + scope.qualifiers.ambulancePickupCity + ', ' + scope.qualifiers.ambulancePickupState + ', ' + scope.qualifiers.ambulancePickupPostal;
                  ambulanceDropAddress = scope.qualifiers.ambulanceDropOffLine1 + ', ' + scope.qualifiers.ambulanceDropOffCity + ', ' + scope.qualifiers.ambulanceDropOffState + ', ' + scope.qualifiers.ambulanceDropOffPostal;
                }
                if (scope.recordTypeCode === 'military') {
                  militaryBranch = scope.qualifiers.militaryBranch;
                  militaryServiceNumber = scope.qualifiers.militaryServiceNumber;
                }
                if (scope.recordTypeCode === 'va') {
                  va = (scope.qualifiers.vaMedicalRecords[0] ? scope.qualifiers.vaMedicalRecords[0] : '')
                    + (scope.qualifiers.vaBenefitRecords[0] ? ', ' + scope.qualifiers.vaBenefitRecords[0] : '');
                }
                if (scope.recordTypeCode === 'union') {
                  unionMembershipNumber = scope.qualifiers.unionMembershipNumber || '--';
                }
                if (scope.recordTypeCode === 'other') {
                  otherDescriptionOfRecords = scope.qualifiers.otherDescriptionOfRecords || '--';
                }
                if (scope.recordTypeCode === 'diagnostic-images') {
                  diagnosticTypeCodes = scope.qualifiers.diagnosticTypeCodes;
                  diagnosticFocusArea = scope.qualifiers.diagnosticFocusArea;
                  diagnosticComments = scope.qualifiers.diagnosticComments;
                }
                if (scope.recordTypeCode === 'insurance') {
                  insuranceClaimNumber = scope.qualifiers.insuranceClaimNumber;
                  insurancePolicyNumber = scope.qualifiers.insurancePolicyNumber;
                }
                if (scope.recordTypeCode === 'employment') {
                  employmentSSN = this.mapSSNforRecord(retrievalData, requestNumber);
                }
                if (scope.recordTypeCode === 'irs') {
                  irsSSN = this.mapSSNforRecord(retrievalData, requestNumber);
                }
                if (scope.recordTypeCode === 'social-security') {
                  socialSecuritySSN = this.mapSSNforRecord(retrievalData, requestNumber);
                  ssnAddress = this.mapRecordAddress(retrievalData, requestNumber);
                }
              });

              subpoenaLanguage = request.subpoenaLanguage;
              notes = request.notes;

            } // if request number matches scope
          });

          providerInfo = {
            provider: provider,
            address: address,
            email: email,
            recordSubject: recordSubject,
            recordRange: recordRange,
            recordType: recordType,
            subpoenaLanguage: subpoenaLanguage,
            notes: notes,
            doctor: '',
            ambulance: {
              date: ambulanceDateOfService,
              time: ambulanceTimeOfService,
              pickupAddress: ambulancePickupAddress,
              dropAddress: ambulanceDropAddress
            },
            imaging: {
              type: diagnosticTypeCodes,
              others: diagnosticComments,
              specificBodyPart: diagnosticFocusArea
            },
            employment: {
              ss: employmentSSN
            },
            insurance: {
              claimNumber: insuranceClaimNumber,
              policyNumber: insurancePolicyNumber
            },
            irs: {
              ss: irsSSN
            },
            military: {
              branch: militaryBranch,
              serviceNumber: militaryServiceNumber
            },
            socialSecurity: {
              ss: socialSecuritySSN,
              address: ssnAddress
            },
            union: {
              membershipNumber: unionMembershipNumber
            },
            va: {
              va: va
            },
            other: {
              descriptionOfRecords: otherDescriptionOfRecords
            }
          };

          result.providers.set(requestNumber, providerInfo);

        }); // for requests
      } // if
    }); // for roles
  }

  addMedicalProviderDetailsToDisplay(element: any, result: OrderDetails) {
    let doctor: string = element.party.displayName;

    element.roles.forEach((role: any) => {
      role.requestNumbers.forEach((requestNumber: any) => {
        let provider = result.providers.get(requestNumber);
        if (provider) {
          provider.doctor = doctor;
        }
      });
    });
  }

  getSubject(orderDetails: RootObject) {
    return orderDetails.retrievalObjective.parties?.find(p => p.roles?.some(r => r.requestNumbers));
  }

  mapSSNforRecord(retrievalData: any, requestNumber: number) {
    let ssn: string = '';
    retrievalData.parties.forEach((party: any) => {
      party.roles.forEach((role: any) => {
        if (role.roleCode === 'record-subject' && role.requestNumbers?.includes(requestNumber)) {
          ssn = role.recordSubject.socialSecurityNumber;
        }
      });
    });
    return ssn;
  }

  mapRecordAddress(retrievalData: any, requestNumber: number) {
    let ssnAddress: string = '';
    retrievalData.parties.forEach((partys: any) => {
      if ((partys.party.locators.length > 0) && partys.party.locators[0].location && partys.roles[0].requestNumbers?.includes(requestNumber)) {
        ssnAddress = (partys.party.locators[0].location.location.usAddress.lineOne ? partys.party.locators[0].location.location.usAddress.lineOne + ', ' : '')
          + (partys.party.locators[0].location.location.usAddress.city ? partys.party.locators[0].location.location.usAddress.city + ', ' : '')
          + (partys.party.locators[0].location.location.usAddress.stateCode ? this.getStateDescription(partys.party.locators[0].location.location.usAddress.stateCode, this.vocabData) + ', ' : '')
          + (partys.party.locators[0].location.location.usAddress.postalCode ? partys.party.locators[0].location.location.usAddress.postalCode : '');
      }
    });
    return ssnAddress;
  }

}
